import type { FC } from "react";

import Box, { type BoxProps } from "@mui/material/Box";

import { css, keyframes } from "@emotion/react";
import emotionStyled from "@emotion/styled";
import darkTheme from "@styles/theme/darkThemeOptions";

interface EmptySpaceProps extends BoxProps {
	background?: string;
	backgroundColor?: string;
	size?: number;
	strokeWidth?: number;
	strokeHalfWidth?: number;
}

const movingBackgroundAnimation = keyframes`
  0% { background-position: 0% 50% }
  50% { background-position: 100% 50% }
  100% { background-position: 0% 50% }
`;

const EmptyComponent = emotionStyled(Box)(
	({
		background,
		backgroundColor,
		size,
		strokeHalfWidth,
		strokeWidth,
	}: EmptySpaceProps) => css`
    position: relative;
    height: ${size}px;
    width: ${size}px;
    background: ${background};
    background-size: 400% 400%;
    animation: ${movingBackgroundAnimation} 6s ease infinite;
    border-radius: 100%;
    box-shadow: 0px 0px 10px ${strokeHalfWidth / 8}px ${backgroundColor};

    ::before {
      content: '';
      position: absolute;
      border-radius: 100%;
      height: calc(100% - ${strokeWidth}px);
      width: calc(100% - ${strokeWidth}px);
      top: ${strokeHalfWidth}px;
      left: ${strokeHalfWidth}px;
      background-color: ${backgroundColor};
    }

    ::after {
      content: '';
      position: absolute;
      border-radius: 4px;
      height: calc(100% + 12px);
      width: ${strokeHalfWidth}px;
      transform: rotate(45deg);
      background: ${background};
      background-size: 400% 400%;
      animation: ${movingBackgroundAnimation} 6s ease infinite;
      top: -7px;
      left: calc(50% - ${strokeHalfWidth}px);
      box-shadow: ${strokeHalfWidth / 2}px 0px 10px ${strokeHalfWidth / 5}px
        ${backgroundColor};
    }
  `,
) as FC<EmptySpaceProps>;

const EmptySpace = ({
	background = darkTheme.Color.LazyRainbow,
	backgroundColor = darkTheme.Color.BackgroundOne,
	size = 100,
	strokeWidth = 8,
}) => {
	const strokeHalfWidth = strokeWidth / 2;

	return (
		<EmptyComponent
			background={background}
			backgroundColor={backgroundColor}
			size={size}
			strokeWidth={strokeWidth}
			strokeHalfWidth={strokeHalfWidth}
		/>
	);
};

export default EmptySpace;
